import { t } from '@lingui/core/macro';
import { cloudManager, connectionManager, ContextEventType, dataContext, type IEntity, TypeId } from '@sdk';
import { subscribeToEntityOps } from '@sdk/FlowSync/entity-ops';
import { DockPointer } from '@src/navigation/DockPointer';
import { ViewType } from '@src/types/ViewType';
import type { ErrorDetail, NotificationAction } from './types';
import { dismiss, notify } from './notify';

/**
 * Maps backend WS signals into `notify()` calls — the single ingest point.
 *
 * Hub-error toast storms are fixed here: every hub error of a given status
 * class uses ONE stable id, so N broadcasts collapse to one live toast that
 * updates in place (was one sonner toast per event). A 401 only toasts when a
 * previously-valid session lapsed ("Cloud sign-in expired"); the plain
 * logged-out state stays silent (login CTA overlay covers it).
 *
 * Owns: the `hub_client_error` listener, the one-shot bootstrap notice, and the
 * `on_flow_data` hook_op listener (skill / incoming-task badges) that used to
 * live in `use-notification-store.ts`.
 */

// --- hub client errors -------------------------------------------------------

interface HubClientErrorMsg {
  method?: string;
  path?: string;
  status_code?: number;
  message?: string;
  suppressed_count?: number;
  /** The error's details, built by the backend in the `ErrorDetail` shape. */
  detail?: ErrorDetail;
}

// What holds each cloud toast up: the failed requests it reports ("GET /path") and, for
// the shared ids, the connection slot. A toast goes away only once EVERY holder got an
// answer from the hub — a request that never did keeps its toast up.
const CONNECTION_HOLDER = 'connection';
const toastHolders = new Map<string, Set<string>>();

function hold(toastId: string, holder: string): void {
  const holders = toastHolders.get(toastId) ?? new Set<string>();
  holders.add(holder);
  toastHolders.set(toastId, holders);
}

function release(holder: string): void {
  for (const [toastId, holders] of toastHolders) {
    if (!holders.delete(holder) || holders.size > 0) continue;
    toastHolders.delete(toastId);
    dismiss(toastId);
  }
}

function handleHubClientRecovered(msg: { method?: string; path?: string }): void {
  release(`${String(msg.method ?? '').trim()} ${String(msg.path ?? '').trim()}`);
}

function handleHubClientError(msg: HubClientErrorMsg): void {
  const method = String(msg.method ?? '').trim();
  const path = String(msg.path ?? '').trim();
  const statusCode = Number(msg.status_code ?? 0);
  const rawMessage = String(msg.message ?? '');
  const suppressed = Number(msg.suppressed_count ?? 0);
  const request = `${method} ${path}`;

  if (suppressed > 0) {
    notify.warning({
      id: 'cloud-errors-suppressed',
      title: t`Hub errors suppressed`,
      message: t`${suppressed} hub errors were suppressed in the current window.`,
      detail: msg.detail,
    });
    return;
  }

  // Everything known about the failure, behind the alert's Details button — not the headline.
  const detail = msg.detail;

  if (statusCode === 0) {
    notify.error({
      id: 'cloud-unreachable',
      title: t`Cloud is not available`,
      message: t`We couldn't reach the cloud service. Check your connection or try again in a moment.`,
      detail,
    });
    hold('cloud-unreachable', request);
  } else if (statusCode === 401) {
    // A 401 while we were never authenticated (or after an explicit logout) is
    // the *normal* logged-out state — not an error. The stream inbox/conversation
    // surfaces show a Login CTA overlay for that case, so swallow the toast.
    // Only surface "sign-in expired" when a previously-valid cloud session
    // actually lapsed (we still believe we're logged in) — without it the
    // instance drifts into a silently signed-out state and every cloud action
    // fails with an unexplained 401/403 later.
    if (cloudManager.loginStatus !== 'logged_in') return;
    notify.error({
      id: 'cloud-auth-expired',
      title: t`Cloud sign-in expired`,
      message: t`Please sign in again to keep using cloud features.`,
      actions: [{ label: t`Sign in`, command: 'cloud.signin' }],
      detail,
    });
    hold('cloud-auth-expired', request);
  } else if (statusCode === 403) {
    notify.error({
      id: 'cloud-access-denied',
      title: t`Cloud access denied`,
      message: t`You don't have permission for this action. Contact your admin if this seems wrong.`,
      detail,
    });
    hold('cloud-access-denied', request);
  } else if (statusCode === 404) {
    notify.warning({
      id: 'cloud-not-found',
      title: t`Cloud resource not found`,
      message: t`We couldn't find what you were looking for on the cloud.`,
      detail,
    });
    hold('cloud-not-found', request);
  } else if (statusCode >= 500) {
    notify.error({
      id: 'cloud-server-error',
      title: t`Cloud service is having trouble`,
      message: t`The cloud service returned an error. Please try again in a moment.`,
      detail,
    });
    hold('cloud-server-error', request);
  } else if (statusCode >= 400) {
    // Benign rejection: inviting someone who already accepted means the desired
    // state (they're a member) already holds, and the invite dialog surfaces its
    // own contextual error — the generic toast on top is pure noise.
    if (/already accepted/i.test(rawMessage)) {
      console.warn(`[cloud] hub rejected ${method} ${path} (${statusCode}) — benign: ${rawMessage}`);
      return;
    }
    notify.error({
      id: 'cloud-request-rejected',
      title: t`Cloud request rejected`,
      message: rawMessage || `The cloud rejected the request (${statusCode}).`,
      detail,
    });
    hold('cloud-request-rejected', request);
  }
}

// --- cloud connection slot ---------------------------------------------------

/**
 * The `hub_client_error` path above reacts to a FAILED hub HTTP request. This
 * reacts to the connection STATUS SLOT, so an outage that never rides a request
 * (WS connection dropped, or a box that boots unreachable and makes no cloud
 * call) still surfaces. Reuses the SAME toast ids as the request path so the two
 * collapse to one live toast instead of double-firing, and clears on recovery.
 * Reads the authoritative slot rather than the event payload. Desktop-only.
 */
function handleCloudConnectionStatus(): void {
  if (!cloudManager.connectionControlsAvailable) return;
  const status = cloudManager.connectionSlot.status;
  if (status === 'error') {
    notify.error({
      id: 'cloud-unreachable',
      title: t`Cloud is not available`,
      message: t`We couldn't reach the cloud service. Check your connection or try again in a moment.`,
    });
    hold('cloud-unreachable', CONNECTION_HOLDER);
  } else if (status === 'auth_rejected') {
    notify.error({
      id: 'cloud-auth-expired',
      title: t`Cloud sign-in expired`,
      message: t`Please sign in again to keep using cloud features.`,
      actions: [{ label: t`Sign in`, command: 'cloud.signin' }],
    });
    hold('cloud-auth-expired', CONNECTION_HOLDER);
  } else if (status === 'connected' || status === 'verified') {
    // The hub answered the connection; a toast a failed REQUEST also holds stays up.
    release(CONNECTION_HOLDER);
  }
}

// --- bootstrap notice (one-shot) ---------------------------------------------

let bootstrapNoticeShown = false;
function flushBootstrapNotice(): void {
  if (bootstrapNoticeShown) return;
  const notice = dataContext.bootstrapInfo?.notice;
  if (!notice) return;
  bootstrapNoticeShown = true;
  notify({
    id: notice.id,
    level: notice.level,
    title: notice.title,
    message: notice.message,
    durationMs: 12000,
  });
}

// --- skill / incoming-task badges (hook_op flow data) ------------------------

const HOOK_OP = 'hook_op';

async function handleFlowData(_typeId: unknown, flowData: Record<string, unknown>): Promise<void> {
  const attributes = flowData.attributes as Record<string, string> | undefined;
  if (attributes?.webhook_type !== HOOK_OP) return;

  const flowValue = flowData.flow_value as Record<string, unknown> | undefined;
  const rsType = flowValue?.type as string | undefined;
  const rsOp = flowValue?.operation as string | undefined;
  const rsData = flowValue?.data as Record<string, unknown> | undefined;
  const eventName = rsData?.event_name as string | undefined;
  const eventData = rsData?.event_data as Record<string, unknown> | undefined;
  const context = eventData?.context as { skill_name?: string; session_id?: string; cwd?: string } | undefined;

  if (rsType === 'skill' && rsOp === 'event' && eventName) {
    if (eventName === 'skill_activated') {
      const meta = (eventData?.notification ?? {}) as { skill_name?: string };
      if (meta.skill_name) {
        notify({
          id: `skill-activated:${meta.skill_name}`,
          level: 'info',
          title: meta.skill_name,
          category: ViewType.ASSETS,
          actions: [{ label: t`View`, href: '/dock/assets/list/skill' }],
        });
      }
    } else if (eventName === 'started_generating_skill' && context?.skill_name) {
      // Keyed on session so `skill_ready` upgrades this badge in place.
      notify({
        id: `skill:${context.session_id ?? context.skill_name}`,
        level: 'info',
        busy: true,
        title: t`Generating: ${context.skill_name}`,
        category: ViewType.ASSETS,
        actions: context.session_id
          ? [
              {
                label: t`View Session`,
                command: 'terminal.resume',
                args: { sessionId: context.session_id, ...(context.cwd ? { cwd: context.cwd } : {}) },
              },
            ]
          : undefined,
      });
    } else if (eventName === 'skill_ready' && context?.skill_name) {
      notify({
        id: `skill:${context.session_id ?? context.skill_name}`,
        level: 'success',
        title: t`Ready: ${context.skill_name}`,
        category: ViewType.ASSETS,
        actions: [{ label: t`View Skill`, href: '/dock/assets/list/skill' }],
      });
    }
  }

  if (rsType === 'notification' && rsOp === 'create') {
    const ev = (rsData?.event_data ?? {}) as Record<string, unknown>;
    const taskTypeId = ev.task_type_id as string | undefined;
    const specType = (ev.spec_type as string | undefined) ?? 'plan';
    const senderName = (ev.sender_name as string | undefined) ?? 'Someone';
    const taskId = ev.task_id as string | undefined;
    const taskTitle = `New ${specType} from ${senderName}`;

    notify({
      id: `incoming-task:${taskId ?? taskTitle}`,
      level: 'info',
      title: taskTitle,
      category: ViewType.TASKS,
      typeId: taskTypeId,
      actions: [
        { label: t`View`, href: taskTypeId ? `/dock/${ViewType.TASKS}/${taskTypeId}` : `/dock/${ViewType.TASKS}` },
      ],
    });

    // Side-effect: open the incoming-task dialog (kept out of `notify`).
    if (taskId) {
      const { useIncomingTaskStore } = await import('@src/store/use-incoming-task-store');
      useIncomingTaskStore.getState().setPendingTask({ taskId, taskTitle, senderName });
    }
  }
}

// Event-bus adapter: the bus expects a void listener; fire-and-forget the
// async handler through one stable reference so off() can unsubscribe it.
function onFlowDataEvent(typeId: unknown, flowData: Record<string, unknown>): void {
  void handleFlowData(typeId, flowData);
}

// --- a help request / message that did not reach the hub ---------------------

/** The failure kind each message last told the person about — so a retry that fails the same way
 *  (every startup, sign-in and reconnect retries) does not toast again. */
const deliveryToldKind = new Map<string, string>();

function onFlowMessageOp(typeId: TypeId, _op: unknown, data: IEntity | undefined): void {
  const fm = data as
    | {
        outbound?: boolean;
        delivery_status?: string;
        body_status?: string;
        conversation_id?: string | null;
        delivery_failure?: { kind: string; message: string } | null;
      }
    | undefined;
  if (!fm?.outbound) return;
  const owed = fm.delivery_status === 'created' || fm.delivery_status === 'pending_send' || fm.body_status === 'failed';
  const failure = owed ? fm.delivery_failure : null;
  const key = typeId.id;
  if (!failure) {
    if (deliveryToldKind.delete(key)) dismiss(`delivery:${key}`);
    return;
  }
  if (deliveryToldKind.get(key) === failure.kind) return;
  deliveryToldKind.set(key, failure.kind);
  const open: NotificationAction[] = fm.conversation_id
    ? [{ label: t`Open`, href: DockPointer.forConversation(fm.conversation_id).toUrl() }]
    : [];
  notify({
    id: `delivery:${key}`,
    level: failure.kind === 'signed_out' || failure.kind === 'offline' ? 'warning' : 'error',
    title: failure.kind === 'signed_out' ? t`Saved — it will send after you sign in` : t`Not sent yet`,
    message: failure.message,
    actions: failure.kind === 'signed_out' ? [{ label: t`Sign in`, command: 'cloud.signin' }, ...open] : open,
    forceToast: true,
  });
}

/** Wire all WS-driven notifications. Call once at app start; returns a cleanup fn. */
export function initNotificationIngest(): () => void {
  flushBootstrapNotice();
  dataContext.on(ContextEventType.CONTEXT_CHANGED, flushBootstrapNotice);
  cloudManager.on('hub_client_error', handleHubClientError);
  cloudManager.on('hub_client_recovered', handleHubClientRecovered);
  cloudManager.on('connection_status_changed', handleCloudConnectionStatus);
  connectionManager.on('on_flow_data', onFlowDataEvent);
  const offFlowMessages = subscribeToEntityOps('flow_message', onFlowMessageOp, { ops: ['update', 'create'] });
  // Catch a box that already booted into an errored connection before this wired up.
  handleCloudConnectionStatus();

  // Dev-only test bridge: lets browser automation drive the real ingest path
  // (hub errors, hook_op flow data) and the dispatcher directly. Stripped from
  // production builds.
  if (import.meta.env?.DEV) {
    (window as unknown as Record<string, unknown>).__notifyTest = {
      notify,
      dismiss: notify.dismiss,
      hubError: (msg: HubClientErrorMsg) => handleHubClientError(msg),
      flowData: (flowData: Record<string, unknown>) => handleFlowData(undefined, flowData),
    };
  }

  return () => {
    dataContext.off(ContextEventType.CONTEXT_CHANGED, flushBootstrapNotice);
    cloudManager.off('hub_client_error', handleHubClientError);
    cloudManager.off('hub_client_recovered', handleHubClientRecovered);
    cloudManager.off('connection_status_changed', handleCloudConnectionStatus);
    connectionManager.off('on_flow_data', onFlowDataEvent);
    offFlowMessages();
  };
}
